import { create } from "zustand";

import { readinessApi } from "../lib/api";
import type { ReadinessActiveResponse } from "../lib/readiness-types";

// SILL-255: shared readiness cache.
//
// Why a Zustand store instead of per-page hook state:
//   * `useReadiness` previously held local React state, so navigating between
//     `/dashboard`, `/dashboard/settings`, and `/calculators` unmounted and
//     remounted the hook, dropping cached data and triggering a fresh fetch.
//   * The dashboard's `readinessTokenResolver` recomputed on every view
//     switch, retriggering the effect.
//   * Both behaviors caused the sidebar score and refresh overlay to flicker
//     on passive navigation.
//
// Store responsibilities:
//   * `ensureLoaded(getToken)` — fetch on first request, no-op afterwards.
//     Concurrent callers share a single in-flight promise so multiple pages
//     mounting at the same time still issue exactly one network call.
//   * `refresh(getToken)` — explicit revalidation called only by mutation
//     handlers (target edit, profile edit, property changes). Existing data
//     stays present during the refetch; consumers branch on `isFetching` vs
//     `hasLoaded` to decide between an empty-state spinner and a quiet
//     background update.
//   * `reset()` — clears state for sign-out / test isolation.
//
// `data` is preserved on error so a transient failure doesn't blank the
// sidebar score.
type ReadinessStore = {
  data: ReadinessActiveResponse | null;
  error: Error | null;
  isFetching: boolean;
  hasLoaded: boolean;

  ensureLoaded: (
    getToken: () => Promise<string | null | undefined>,
  ) => Promise<void>;
  refresh: (
    getToken: () => Promise<string | null | undefined>,
  ) => Promise<void>;
  reset: () => void;
};

// Module-scoped reference to the in-flight fetch. Lives outside the store
// state so we don't trigger subscriber re-renders just for promise tracking.
let inFlight: Promise<void> | null = null;

async function runFetch(
  getToken: () => Promise<string | null | undefined>,
  set: (
    partial:
      | Partial<ReadinessStore>
      | ((state: ReadinessStore) => Partial<ReadinessStore>),
  ) => void,
): Promise<void> {
  set({ isFetching: true });
  try {
    const token = (await getToken()) ?? undefined;
    const response = await readinessApi.getActive(token);
    set({
      data: response,
      error: null,
      hasLoaded: true,
      isFetching: false,
    });
  } catch (e) {
    // Keep `data` and `hasLoaded` so a transient error doesn't blank an
    // already-rendered sidebar score.
    set({ error: e as Error, isFetching: false });
  }
}

export const useReadinessStore = create<ReadinessStore>((set, get) => ({
  data: null,
  error: null,
  isFetching: false,
  hasLoaded: false,

  ensureLoaded: async (getToken) => {
    const state = get();
    if (state.hasLoaded) return;
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  refresh: async (getToken) => {
    if (inFlight) return inFlight;
    inFlight = runFetch(getToken, set).finally(() => {
      inFlight = null;
    });
    return inFlight;
  },

  reset: () => {
    inFlight = null;
    set({
      data: null,
      error: null,
      hasLoaded: false,
      isFetching: false,
    });
  },
}));
